/* eslint-disable no-unused-vars */
import { createMiddleware } from 'redux-beacon';
import combineEvents from '@redux-beacon/combine-events';

import { isEmployeeRate } from 'BookingFlow/utils';
import { API_REQUEST_STATES } from 'store/apiRequestStates/constants';
import { fetchAppStatusFulfilled } from 'store/appStatus/appStatus.slice';
import {
	fetchPropertyContentFailed,
	fetchPropertyContentFulfilled,
} from 'store/propertyContent/propertyContent.slice';
import { selectPropertyContent } from 'store/propertyContent/propertyContent.selectors';
import {
	fetchPropertyWebCheckin,
	fetchPropertyWebCheckinFailed,
	fetchPropertyWebCheckinFulfilled,
} from 'store/propertyWebCheckin/propertyWebCheckin.slice';
import { selectPropertyWebCheckin } from 'store/propertyWebCheckin/propertyWebCheckin.selectors';
import {
	confirmCheckIn,
	confirmCheckInCancel,
	confirmCheckInFailed,
	confirmCheckInFulfilled,
	fetchBooking,
	fetchBookingCancel,
	fetchBookingFailed,
	fetchBookingFulfilled,
	webCheckInErrorEvt,
} from 'store/webCheckIn/webCheckIn.slice';
import {
	selectBooking,
	selectReservationIsPast,
	selectValidTimeToCheckIn,
	selectWebCheckInEnabled,
} from 'store/webCheckIn/webCheckIn.selectors';

import {
	addPackage,
	addPackageCancel,
	addPackageFailed,
	addPackageFulfilled,
	cancelBooking,
	cancelBookingCancel,
	cancelBookingFailed,
	cancelBookingFulfilled,
	completeBooking,
	completeBookingCancel,
	completeBookingFailed,
	completeBookingFulfilled,
	submitBookingPreferences,
	submitBookingPreferencesCancel,
	submitBookingPreferencesFailed,
	submitBookingPreferencesFulfilled,
} from 'store/bookings';

import {
	apmTarget,
	apmCaptureError,
	customTransaction,
	endTransaction,
	apmAddLabels,
	apmSetUserContext,
} from 'config/elasticApm';
import {
	fetchSearchResults,
	fetchSearchResultsFailed,
	fetchSearchResultsForResultSetIdFulfilled,
	fetchSearchResultsFulfilled,
} from './searchResults';
import {
	generateOtp,
	generateOtpCancel,
	generateOtpFailed,
	generateOtpFulfilled,
	submitOtp,
	submitOtpCancel,
	submitOtpFailed,
	submitOtpFulfilled,
} from './signIn';

function getUserType(searchParams) {
	return searchParams?.ppMode ? 'preferred' : isEmployeeRate(searchParams) ? 'employee' : 'guest';
}

function getErrorType({ errors, supplierErrors, apiErrors }) {
	if (supplierErrors?.[0]?.errorCode === 'E0301' || apiErrors?.[0]?.errorCode === 'E0301') {
		return 'no_availability';
	}
	if (supplierErrors?.[0]) {
		return 'supplier';
	}
	if (apiErrors?.[0] || errors?.[0]?.statusCode === 400) {
		return 'validation';
	}
	return 'unknown';
}

const LOCATE_RESERVATION = 'Checkin - Locate Reservation';
const CONFIRM_CHECK_IN = 'Checkin - Confirm';

// every check-in step shares this label so they can be queried together
const checkinTransaction = name =>
	combineEvents(
		customTransaction(name),
		apmAddLabels(name, () => ({ flow: 'checkin' }))
	);

const when = (predicate, eventDef) => (action, prevState, nextState) =>
	predicate(action, prevState, nextState) ? eventDef(action, prevState, nextState) : undefined;

// the guest's golden id identifies them in APM once their booking is known
const REQUESTED_HOTEL_CODE_MAX_LENGTH = 10;

// guest input, so cap it before it reaches APM; String() also covers a repeated query param parsed as an array
function getRequestedHotelCodeLabels(requestedHotelCode) {
	if (!requestedHotelCode) {
		return undefined;
	}
	const code = String(requestedHotelCode);
	return {
		requestedHotelCode:
			code.length > REQUESTED_HOTEL_CODE_MAX_LENGTH
				? `${code.slice(0, REQUESTED_HOTEL_CODE_MAX_LENGTH)}...`
				: code,
	};
}

const getBookingHotelCode = state => selectBooking(state)?.hotelProducts?.[0]?.hotelCode;

// the property the guest is checking in to, once the booking is known
function getBookingLabels(state) {
	const hotelCode = getBookingHotelCode(state);
	return hotelCode ? { hotelCode } : undefined;
}

function getGuestUserContext(state) {
	const goldenId = selectBooking(state)?.customers?.[0]?.goldenId;
	return goldenId ? { id: goldenId } : undefined;
}

// failures that come from the guest or planned downtime rather than something broken; everything else is 'unexpected'
const EXPECTED_LOOKUP_FAILURES = [
	'not_found',
	'past_reservation',
	'check_in_window_not_open',
	'not_enrolled',
	'invalid_property',
];
const EXPECTED_CONFIRM_ERROR_CODES = ['invalid', 'ready', 'processed'];
const EXPECTED_OFFLINE_CAUSES = ['offline_flag'];

const checkinFailure = (isExpected, labels) => ({
	outcome: 'failure',
	errorClass: isExpected ? 'expected' : 'unexpected',
	...labels,
});

const lookupFailure = (failureReason, labels) =>
	checkinFailure(EXPECTED_LOOKUP_FAILURES.includes(failureReason), { failureReason, ...labels });

function getCheckInWindow(state) {
	if (selectReservationIsPast(state)) {
		return 'past';
	}
	return selectValidTimeToCheckIn(state) ? 'open' : 'not_open';
}

// outcome of the reservation lookup as the guest sees it, undefined until the booking and property data are in
function getLookupResolution(state) {
	const hotelCode = getBookingHotelCode(state);
	if (!hotelCode) {
		return undefined;
	}
	if (
		state.propertyContent?.error?.[hotelCode] ||
		state.apiRequestStates?.[fetchPropertyWebCheckin.type] === API_REQUEST_STATES.FAILED
	) {
		return lookupFailure('property_content_error');
	}
	if (
		!selectPropertyContent(hotelCode)(state) ||
		!selectPropertyWebCheckin({ propertyCode: hotelCode })(state)
	) {
		return undefined;
	}

	const checkInWindow = getCheckInWindow(state);
	if (checkInWindow === 'past') {
		return lookupFailure('past_reservation');
	}
	if (checkInWindow === 'not_open') {
		return lookupFailure('check_in_window_not_open');
	}
	if (!selectWebCheckInEnabled(hotelCode)(state)) {
		return lookupFailure('not_enrolled');
	}
	return { outcome: 'success' };
}

const resolveLookup = when(
	(action, prevState, nextState) => getLookupResolution(nextState),
	combineEvents(
		apmAddLabels(LOCATE_RESERVATION, (action, prevState, nextState) =>
			getLookupResolution(nextState)
		),
		endTransaction(LOCATE_RESERVATION)
	)
);

const eventsMap = {
	[generateOtp.type]: customTransaction('Generate OTP'),
	[generateOtpCancel.type]: endTransaction('Generate OTP'),
	[generateOtpFailed.type]: endTransaction('Generate OTP', 'failure'),
	[generateOtpFulfilled.type]: endTransaction('Generate OTP', 'success'),

	[submitOtp.type]: customTransaction('Submit OTP'),
	[submitOtpCancel.type]: endTransaction('Submit OTP'),
	[submitOtpFailed.type]: endTransaction('Submit OTP', 'failure'),
	[submitOtpFulfilled.type]: endTransaction('Submit OTP', 'success'),

	[fetchSearchResults.type]: combineEvents(
		customTransaction('Fetch Availability'),
		apmAddLabels('Fetch Availability', ({ payload: { searchParams } }) => ({
			type: getUserType(searchParams),
		}))
	),
	[fetchSearchResultsFailed.type]: combineEvents(
		apmAddLabels('Fetch Availability', ({ payload: { errors, supplierErrors, apiErrors } }) => {
			const errorType = getErrorType({ errors, supplierErrors, apiErrors });

			return {
				errorType,
				outcome:
					errorType === 'no_availability' || errorType === 'validation' ? 'success' : 'failure',
				errorCode:
					supplierErrors?.[0]?.errorCode ||
					apiErrors?.[0]?.errorCode ||
					errors?.[0]?.errorCode ||
					(errors?.[0]?.statusCode ? `HTTP ${errors?.[0]?.statusCode}` : ''),
				error:
					supplierErrors?.[0]?.errorMessage ||
					apiErrors?.[0]?.userMessage ||
					apiErrors?.[0]?.devMessage ||
					errors?.[0]?.message ||
					'Unexpected Error',
			};
		}),
		endTransaction('Fetch Availability')
	),
	[fetchSearchResultsFulfilled.type]: endTransaction('Fetch Availability', 'success'),
	[fetchSearchResultsForResultSetIdFulfilled.type]: endTransaction('Fetch Availability', 'success'),

	[addPackage.type]: customTransaction('Choose Your Room'),
	[addPackageCancel.type]: endTransaction('Choose Your Room'),
	[addPackageFailed.type]: endTransaction('Choose Your Room', 'failure'),
	[addPackageFulfilled.type]: endTransaction('Choose Your Room', 'success'),

	[submitBookingPreferences.type]: customTransaction('Personalize Booking'),
	[submitBookingPreferencesCancel.type]: endTransaction('Personalize Booking'),
	[submitBookingPreferencesFailed.type]: endTransaction('Personalize Booking', 'failure'),
	[submitBookingPreferencesFulfilled.type]: endTransaction('Personalize Booking', 'success'),

	[cancelBooking.type]: customTransaction('Cancel Booking'),
	[cancelBookingCancel.type]: endTransaction('Cancel Booking'),
	[cancelBookingFailed.type]: endTransaction('Cancel Booking', 'failure'),
	[cancelBookingFulfilled.type]: endTransaction('Cancel Booking', 'success'),

	[completeBooking.type]: combineEvents(
		customTransaction('Checkout'),
		apmAddLabels('Checkout', ({ payload: { searchParams } }) => ({
			type: getUserType(searchParams),
		}))
	),
	[completeBookingCancel.type]: endTransaction('Checkout'),
	[completeBookingFulfilled.type]: endTransaction('Checkout', 'success'),
	[completeBookingFailed.type]: combineEvents(
		apmAddLabels('Checkout', ({ payload: { errors, supplierErrors, apiErrors } }) => {
			const errorType = getErrorType({ errors, supplierErrors, apiErrors });

			return {
				errorType,
				outcome: errorType === 'validation' ? 'success' : 'failure',
				errorCode:
					supplierErrors?.[0]?.errorCode ||
					apiErrors?.[0]?.errorCode ||
					errors?.[0]?.errorCode ||
					(errors?.[0]?.statusCode ? `HTTP ${errors?.[0]?.statusCode}` : ''),
				error:
					supplierErrors?.[0]?.errorMessage ||
					apiErrors?.[0]?.userMessage ||
					apiErrors?.[0]?.devMessage ||
					errors?.[0]?.message ||
					'Unexpected Error',
			};
		}),
		apmCaptureError(({ payload: { errorContext } }) => ({
			error: 'makeReservationFailed',
			errorContext,
		})),
		endTransaction('Checkout')
	),

	[fetchBooking.type]: combineEvents(
		checkinTransaction(LOCATE_RESERVATION),
		// what the guest entered, kept apart from the booking's hotelCode so lookups that fail can still be attributed
		apmAddLabels(LOCATE_RESERVATION, ({ payload: { hotelCode } }) =>
			getRequestedHotelCodeLabels(hotelCode)
		)
	),
	[fetchBookingCancel.type]: endTransaction(LOCATE_RESERVATION),
	[fetchBookingFailed.type]: combineEvents(
		apmAddLabels(LOCATE_RESERVATION, ({ payload: { failureReason, errorCode } }) =>
			lookupFailure(failureReason, { errorCode })
		),
		endTransaction(LOCATE_RESERVATION)
	),
	// a found booking can still be unusable (past, outside the window, not enrolled), which needs property data
	[fetchBookingFulfilled.type]: combineEvents(
		apmSetUserContext((action, prevState, nextState) => getGuestUserContext(nextState)),
		apmAddLabels(LOCATE_RESERVATION, (action, prevState, nextState) => getBookingLabels(nextState)),
		resolveLookup
	),
	[fetchPropertyContentFulfilled.type]: resolveLookup,
	[fetchPropertyContentFailed.type]: resolveLookup,
	[fetchPropertyWebCheckinFulfilled.type]: resolveLookup,
	[fetchPropertyWebCheckinFailed.type]: resolveLookup,
	// a deep link with an unknown hotel code never gets as far as a lookup
	[webCheckInErrorEvt.type]: when(
		({ payload }) => payload?.error === 'invalid property',
		combineEvents(
			checkinTransaction(LOCATE_RESERVATION),
			apmAddLabels(LOCATE_RESERVATION, ({ payload: { requestedHotelCode } }) =>
				lookupFailure('invalid_property', getRequestedHotelCodeLabels(requestedHotelCode))
			),
			endTransaction(LOCATE_RESERVATION)
		)
	),

	[confirmCheckIn.type]: combineEvents(
		checkinTransaction(CONFIRM_CHECK_IN),
		// the booking may have come from persisted state rather than a lookup this session
		apmSetUserContext((action, prevState) => getGuestUserContext(prevState)),
		// the 36 hour window is only enforced client side, so record where the submission sits in it
		apmAddLabels(CONFIRM_CHECK_IN, (action, prevState) => ({
			...getBookingLabels(prevState),
			checkInWindow: getCheckInWindow(prevState),
		}))
	),
	[confirmCheckInCancel.type]: endTransaction(CONFIRM_CHECK_IN),
	[confirmCheckInFulfilled.type]: endTransaction(CONFIRM_CHECK_IN, 'success'),
	[confirmCheckInFailed.type]: combineEvents(
		apmAddLabels(CONFIRM_CHECK_IN, ({ payload: { errorCode, error } }) =>
			checkinFailure(EXPECTED_CONFIRM_ERROR_CODES.includes(errorCode), { errorCode, error })
		),
		endTransaction(CONFIRM_CHECK_IN)
	),

	// the status check gates both check-in steps, so fail whichever one is in flight
	[fetchAppStatusFulfilled.type]: when(
		({ meta }) => meta?.offlineCause,
		combineEvents(
			...[LOCATE_RESERVATION, CONFIRM_CHECK_IN].flatMap(name => [
				apmAddLabels(name, ({ meta: { offlineCause } }) =>
					checkinFailure(EXPECTED_OFFLINE_CAUSES.includes(offlineCause), {
						failureReason: 'system_offline',
						offlineCause,
					})
				),
				endTransaction(name),
			])
		)
	),
};

export default createMiddleware(eventsMap, apmTarget());
